"use client"

import Image from "next/image"
import Link from "next/link"
import { useTranslations } from "@/lib/intl/use-translations"
import { BookingCta } from "@/components/ui/booking-cta"
import { Button, ArrowRightIcon } from "@/components/ui/button"
import { useCountry } from "@/components/layout/country-provider"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { CountryGate } from "@/components/layout/country-gate"
import { useSectionGate } from "@/components/layout/section-visibility-provider"
import { track } from "@/lib/analytics"
import { homeKeys as k } from "@/i18n/home-keys"
import { blobMediaUrl } from "@/lib/blob-url"
import { useBookingFlow } from "@/components/layout/booking-flow-provider"
import { useInsurers } from "@/components/layout/insurers-provider"
import { InsurerMarqueeSection } from "@/components/ui/insurer-marquee-section"

// Swiss insurers confirmed as covering Ahead check-ups. Order mirrors
// aheadhealth.com/ch/en/insurance (kpt, atupri, CSS, visana, Groupe Mutuel,
// SWICA). Widths come from the source assets — setting only width keeps the
// stock aspect ratio without stretching. Groupe Mutuel + SWICA reference the
// code-coupled public/icons assets (already used by the /insurance page);
// they aren't in the blob store.
const INSURER_LOGOS = [
  {
    src: blobMediaUrl("/images/insurance/logos/kpt.svg"),
    alt: "KPT",
    width: 84,
  },
  {
    src: blobMediaUrl("/images/insurance/logos/atupri.svg"),
    alt: "Atupri",
    width: 103,
  },
  {
    src: blobMediaUrl("/images/insurance/logos/css.svg"),
    alt: "CSS",
    width: 73,
  },
  {
    src: blobMediaUrl("/images/insurance/logos/visana.svg"),
    alt: "Visana",
    width: 123,
  },
  {
    src: "/icons/groupe-mutuel.svg",
    alt: "Groupe Mutuel",
    width: 59,
  },
  {
    src: "/icons/swica-color.png",
    alt: "SWICA",
    width: 129,
  },
  {
    src: "/icons/egk-color.png",
    alt: "EGK",
    width: 50,
  },
]

interface InsuranceSectionProps {
  /** Passed to analytics for CTA clicks. Defaults to "insurance-section". */
  trackingLocation?: string
  /** Inner container max-width utility (e.g. "max-w-[960px]"). Defaults to
   *  "max-w-[1440px]". */
  maxWidthClassName?: string
  /** Horizontal padding on the inner container. Defaults to "px-4 md:px-10". */
  containerPaddingClassName?: string
  /** Show the secondary "talk to our team / contact" button. Default true. */
  showContactCta?: boolean
}

/**
 * Reusable insurance section mirroring the aheadhealth.com reference:
 * photo on the left, dark aubergine panel on the right with heading +
 * description + insurer-logo row (CH only) + "check coverage" / "contact"
 * CTAs. Used on the homepage and intended for reuse on pricing /
 * how-it-works where the section is currently duplicated inline.
 *
 * Hidden entirely on NL (no reimbursement market — AHD-6780). On other
 * non-CH markets that keep the section, the Swiss-insurer row is hidden and
 * the primary CTA swaps to the booking/waitlist flow since /insurance is
 * CH-only.
 */
export function InsuranceSection(props: InsuranceSectionProps) {
  // The membership-first site shows insurance the way its homepage does. Its
  // ad pages keep the current booking flow and, with it, this panel, so the
  // flow is the switch: "membership-first" is exactly the new site's main
  // pages.
  return useBookingFlow() === "membership-first" ? (
    <MembershipFirstInsurance {...props} />
  ) : (
    <ClassicInsuranceSection {...props} />
  )
}

/**
 * The homepage's insurer marquee, in this page's content column. A page that
 * names its `trackingLocation` gets the classic panel's coverage click,
 * `check-coverage` at that location, on the marquee's button, which goes to
 * the same coverage checker. The blood ad pages under `/go/bloodtest` show
 * this panel on the membership-first site and keep their old event with it.
 */
function MembershipFirstInsurance({
  trackingLocation,
  maxWidthClassName = "max-w-[1440px]",
  containerPaddingClassName = "px-4 md:px-10",
}: InsuranceSectionProps) {
  const t = useTranslations("homeV4")
  const lp = useLocalePath()
  const { code: country } = useCountry()
  const isAllowed = useSectionGate()
  const insurers = useInsurers()
  if (!isAllowed("section:insurance", country)) return null
  return (
    <InsurerMarqueeSection
      section={`mx-auto w-full ${maxWidthClassName} ${containerPaddingClassName}`}
      eyebrow={t("insuranceEyebrow")}
      heading={t("insuranceHeading")}
      body={t("insuranceBody")}
      ctaLabel={t("insuranceCta")}
      ctaHref={lp("/insurance")}
      insurers={insurers}
      onCtaClick={
        trackingLocation
          ? () => track.ctaClick("check-coverage", trackingLocation)
          : undefined
      }
    />
  )
}

function ClassicInsuranceSection({
  trackingLocation = "insurance-section",
  maxWidthClassName = "max-w-[1440px]",
  containerPaddingClassName = "px-4 md:px-10",
  showContactCta = true,
}: InsuranceSectionProps) {
  const t = useTranslations("home")
  const lp = useLocalePath()
  const { code: country } = useCountry()
  const isAllowed = useSectionGate()

  // Whole section is hidden in markets without reimbursement positioning
  // (NL). The Swiss-insurer logo row + coverage CTA inside have their own
  // narrower gates below.
  if (!isAllowed("section:insurance", country)) {
    return null
  }

  // Also hidden on DE: the Swiss-style Selbstzahler/reimbursement module
  // doesn't fit the German market and its /insurance coverage checker is
  // CH-only (AHD-6680). Kept as a direct country check rather than dropping
  // "de" from the section:insurance gate, whose reviewKeyPrefixes also scope
  // insurance.* translation review (which should stay unchanged).
  // country-gate-ignore: DE checkup-phase content hide, not a page gate
  if (country === "de") {
    return null
  }

  return (
    <section className="lazy-section py-16 md:py-24">
      <div
        className={`mx-auto w-full ${maxWidthClassName} ${containerPaddingClassName}`}
      >
        <div className="flex flex-col overflow-hidden rounded-[16px] md:flex-row">
          {/* Left: photo */}
          <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[400px] md:w-1/3">
            <Image
              src={blobMediaUrl("/images/homepage/insurance-photo-kpt.avif")}
              alt=""
              fill
              sizes="(max-width: 768px) 100vw, 33vw"
              className="object-cover"
            />
          </div>

          {/* Right: content */}
          <div className="bg-aubergine-900 flex flex-col justify-center p-4 md:w-2/3 md:p-12">
            <div>
              <h2 className="type-display-sm whitespace-pre-line text-white">
                {t(k.insuranceHeading)}
              </h2>
              <p className="type-body-lg mt-4 text-white/80">
                {t(k.insuranceDescription)}
              </p>

              <CountryGate id="section:home.swiss-insurer-logos">
                <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
                  {INSURER_LOGOS.map((logo) => (
                    <Link
                      key={logo.alt}
                      href={lp("/insurance")}
                      onClick={() =>
                        track.ctaClick(
                          `insurer-logo-${logo.alt.toLowerCase()}`,
                          trackingLocation
                        )
                      }
                      aria-label={`Check ${logo.alt} coverage`}
                      className="opacity-70 transition-opacity hover:opacity-100"
                    >
                      <Image
                        src={logo.src}
                        alt={logo.alt}
                        width={logo.width}
                        height={28}
                        style={{
                          height: 24,
                          width: "auto",
                          filter: "brightness(0) invert(1)",
                        }}
                        className="object-contain"
                      />
                    </Link>
                  ))}
                  <Link
                    href={lp("/insurance")}
                    onClick={() =>
                      track.ctaClick("insurer-and-more", trackingLocation)
                    }
                    className="type-body-md text-white/50 italic transition-colors hover:text-white"
                  >
                    {t(k.insuranceMoreLabel)}
                  </Link>
                </div>
              </CountryGate>

              <div className="mt-8 flex flex-col flex-wrap gap-3 sm:flex-row">
                {isAllowed("section:home.insurance-coverage-cta", country) ? (
                  /* The button base is `whitespace-nowrap` + a fixed 44px
                     height, which the French label ("Vérifier la prise en
                     charge par votre assurance", 311px) overflowed on both
                     sides of the 281px full-width mobile button (AHD-10495).
                     Below sm the label wraps and the button grows with it. */
                  <Button
                    variant="primary"
                    className="group text-aubergine-900 w-full bg-white hover:bg-neutral-100 hover:shadow-none focus-visible:ring-white max-sm:h-auto max-sm:min-h-[44px] max-sm:px-6 max-sm:py-2.5 max-sm:whitespace-normal sm:w-auto"
                    onClick={() => {
                      track.ctaClick("check-coverage", trackingLocation)
                      window.location.href = lp("/insurance")
                    }}
                    iconRight={
                      <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                    }
                  >
                    {t(k.insuranceCheckCoverageCta)}
                  </Button>
                ) : (
                  <BookingCta
                    trackingLabel="Book check-up"
                    trackingLocation={trackingLocation}
                    className="type-title-sm text-aubergine-900 inline-flex items-center justify-center rounded-full bg-white px-6 py-3 transition-colors hover:bg-neutral-100"
                  >
                    {t(k.insuranceCheckCoverageCta)}
                  </BookingCta>
                )}
                {showContactCta && (
                  <Button
                    variant="outline"
                    className="border-white/30 bg-transparent text-white hover:border-white/50 hover:bg-white/10 focus-visible:ring-white"
                    onClick={() => {
                      track.ctaClick("Contact", trackingLocation)
                      window.location.href = lp("/contact")
                    }}
                  >
                    {t(k.insuranceContactCta)}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
